import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HeartPulse, Syringe, ShieldAlert, Stethoscope, Search, ExternalLink, AlertTriangle, CalendarClock } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { enfantsApi, echeancesApi } from '../../utils/api';

function ageStr(dn) {
  if (!dn) return '';
  const d = new Date(dn);
  if (isNaN(d)) return '';
  const now = new Date();
  let mois = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) mois--;
  if (mois < 0) return '';
  if (mois < 24) return `${mois} mois`;
  return `${Math.floor(mois / 12)} ans`;
}

const NIVEAU_STYLE = {
  depasse: 'bg-red-100 text-red-700',
  urgent: 'bg-amber-100 text-amber-700',
  proche: 'bg-navy-100 text-navy-700',
  ok: 'bg-slate-100 text-slate-500',
};

export default function SuiviMedicalPage() {
  const { getApiParams, selectedStructure, structures } = useApp();
  const [enfants, setEnfants] = useState([]);
  const [echeances, setEcheances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...getApiParams(), statut: 'actif' };
      const [eRes, ecRes] = await Promise.all([
        enfantsApi.getAll(params),
        echeancesApi.getAll(getApiParams()).catch(() => ({ data: [] })),
      ]);
      setEnfants(eRes.data || []);
      setEcheances((ecRes.data || []).filter(e => e.type === 'vaccin' || e.type === 'pai'));
    } catch { /* silencieux */ }
    setLoading(false);
  }, [getApiParams]);

  useEffect(() => { load(); }, [load]);

  // Prochaine échéance santé (vaccin/PAI) par enfant
  const prochaineParEnfant = useMemo(() => {
    const m = {};
    echeances.forEach(e => {
      if (!e.enfant_id) return;
      const cur = m[e.enfant_id];
      if (!cur || (e.date_echeance || '') < (cur.date_echeance || '')) m[e.enfant_id] = e;
    });
    return m;
  }, [echeances]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = enfants;
    if (s) list = list.filter(e => `${e.prenom || ''} ${e.nom || ''} ${e.medecin_nom || ''} ${e.allergies || ''}`.toLowerCase().includes(s));
    return list;
  }, [enfants, q]);

  // Regroupement par structure quand aucune structure n'est sélectionnée (vue générale)
  const groupes = useMemo(() => {
    if (selectedStructure) return [{ id: selectedStructure, nom: null, enfants: filtered }];
    const byStruct = {};
    filtered.forEach(e => {
      const k = e.structure_id || 0;
      (byStruct[k] = byStruct[k] || []).push(e);
    });
    return Object.entries(byStruct).map(([k, list]) => {
      const st = structures.find(s => String(s.id) === String(k));
      return { id: k, nom: st ? st.nom : 'Sans structure', couleur: st ? st.couleur : '#94a3b8', enfants: list };
    }).sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
  }, [filtered, selectedStructure, structures]);

  // Échéances santé à venir (vaccins/PAI), triées par date — remontées ici depuis l'ancienne page Échéances.
  const echAvenir = useMemo(() => {
    return [...echeances]
      .filter(e => e.date_echeance)
      .sort((a, b) => (a.date_echeance || '').localeCompare(b.date_echeance || ''))
      .slice(0, 12);
  }, [echeances]);

  const stats = useMemo(() => ({
    total: filtered.length,
    allergies: filtered.filter(e => e.allergies && e.allergies.trim()).length,
    pai: filtered.filter(e => e.pai).length,
    echUrgentes: filtered.filter(e => { const p = prochaineParEnfant[e.id]; return p && (p.niveau === 'urgent' || p.niveau === 'depasse'); }).length,
  }), [filtered, prochaineParEnfant]);

  const Tile = ({ icon: Icon, label, value, tint }) => (
    <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tint}`}><Icon size={18} /></div>
      <div>
        <div className="text-2xl font-semibold text-navy-900 leading-none">{value}</div>
        <div className="text-[12px] text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );

  const renderRow = (e) => {
    const p = prochaineParEnfant[e.id];
    return (
      <tr key={e.id} className="border-t border-slate-100 hover:bg-cream-50/60">
        <td className="px-3 py-2.5">
          <Link to={`/enfants/${e.id}`} className="font-medium text-navy-900 hover:text-gold-600 flex items-center gap-1.5">
            {e.prenom} {e.nom}
            <ExternalLink size={12} className="text-slate-300" />
          </Link>
          <div className="text-[11px] text-slate-400">{ageStr(e.date_naissance)}</div>
        </td>
        {!selectedStructure && (
          <td className="px-3 py-2.5">
            <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.structure_couleur || '#94a3b8' }} />
              {e.structure_nom || '—'}
            </span>
          </td>
        )}
        <td className="px-3 py-2.5 text-[12.5px] text-slate-600">
          {e.medecin_nom ? <>{e.medecin_nom}{e.medecin_tel ? <span className="text-slate-400"> · {e.medecin_tel}</span> : null}</> : <span className="text-slate-300">—</span>}
        </td>
        <td className="px-3 py-2.5 text-[12.5px]">
          {e.allergies && e.allergies.trim()
            ? <span className="inline-flex items-center gap-1 text-red-700"><AlertTriangle size={13} /> {e.allergies}</span>
            : <span className="text-slate-300">—</span>}
        </td>
        <td className="px-3 py-2.5 text-center">
          {e.pai
            ? <span className="text-[11px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">PAI</span>
            : <span className="text-slate-300 text-[12px]">—</span>}
        </td>
        <td className="px-3 py-2.5">
          {p
            ? <span className={`inline-flex items-center gap-1 text-[11.5px] font-medium px-2 py-0.5 rounded-full ${NIVEAU_STYLE[p.niveau] || NIVEAU_STYLE.ok}`}>
                <Syringe size={11} /> {p.libelle || p.type} · {p.date_echeance ? new Date(p.date_echeance).toLocaleDateString('fr-FR') : ''}
              </span>
            : <span className="text-slate-300 text-[12px]">—</span>}
        </td>
      </tr>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <HeartPulse className="text-gold-500" size={24} />
        <h1 className="text-2xl font-serif font-semibold text-navy-900">Suivi médical</h1>
      </div>
      <p className="text-slate-500 text-[13.5px] mb-5">
        Vision globale santé {selectedStructure ? 'de la structure' : 'par structure'} : médecin, allergies, PAI et échéances de vaccination. Cliquez un enfant pour ouvrir sa fiche complète.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile icon={Stethoscope} label="Enfants suivis" value={stats.total} tint="bg-navy-50 text-navy-600" />
        <Tile icon={AlertTriangle} label="Avec allergies" value={stats.allergies} tint="bg-red-50 text-red-500" />
        <Tile icon={ShieldAlert} label="PAI en place" value={stats.pai} tint="bg-amber-50 text-amber-600" />
        <Tile icon={Syringe} label="Échéances urgentes" value={stats.echUrgentes} tint="bg-red-50 text-red-500" />
      </div>

      {echAvenir.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-navy-900">
              <CalendarClock size={16} className="text-gold-500" /> Échéances santé à venir
            </div>
            <a href="/echeances" className="text-[12px] text-navy-500 hover:text-navy-800">Voir toutes les échéances →</a>
          </div>
          <div className="flex flex-col gap-1.5">
            {echAvenir.map(e => {
              const dep = e.niveau === 'depasse'; const urg = e.niveau === 'urgent';
              return (
                <div key={e.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${dep ? 'bg-red-50 border-red-200' : urg ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                  {e.type === 'vaccin' ? <Syringe size={14} className="text-navy-400 flex-shrink-0" /> : <ShieldAlert size={14} className="text-amber-500 flex-shrink-0" />}
                  <span className="flex-1 text-[13px] text-slate-700 truncate">{e.libelle || e.type}{e.enfant_nom ? ` — ${e.enfant_nom}` : ''}</span>
                  <span className="text-[11.5px] text-slate-400">{e.structure_nom || ''}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${dep ? 'bg-red-100 text-red-700' : urg ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                    {e.date_echeance ? new Date(e.date_echeance).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un enfant, médecin, allergie…"
          className="form-input w-full pl-9 rounded-xl text-sm" />
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">Aucun enfant actif à afficher.</div>
      ) : (
        <div className="space-y-6">
          {groupes.map(g => (
            <div key={g.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {g.nom && (
                <div className="px-4 py-2.5 bg-cream-50 border-b border-slate-100 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: g.couleur }} />
                  <span className="font-medium text-navy-900 text-[14px]">{g.nom}</span>
                  <span className="text-[12px] text-slate-400">· {g.enfants.length} enfant{g.enfants.length > 1 ? 's' : ''}</span>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2 font-semibold">Enfant</th>
                      {!selectedStructure && <th className="px-3 py-2 font-semibold">Structure</th>}
                      <th className="px-3 py-2 font-semibold">Médecin traitant</th>
                      <th className="px-3 py-2 font-semibold">Allergies</th>
                      <th className="px-3 py-2 font-semibold text-center">PAI</th>
                      <th className="px-3 py-2 font-semibold">Prochaine échéance santé</th>
                    </tr>
                  </thead>
                  <tbody>{g.enfants.map(renderRow)}</tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
